import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CallIcon from "@mui/icons-material/Call";
import Paginations from "../../common/pagination/Paginations";
import { acceptVenderApi, allPendingVenderApi, rejectVenderApi } from "../../api";
import { useSelector } from "react-redux";
import configuration from "../../config/Config";
import UserAvatar from "../../common/avatar/UserAvatar";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import { AdminTable } from "../../common/adminTable";
import StatusChip from "../../common/statusChip/StatusChip";

const PendingApprove = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const [rejectMessage, setRejectMessage] = useState("");
  const [pendingVendorData, setPendingVendorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const handlePendingVendor = async () => {
    try {
      setLoading(true);
      const response = await allPendingVenderApi({ token, currentPage });
      setPendingVendorData(response?.users || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handlePendingVendor();
  }, [currentPage, change]);

  const handleAcceptVendor = async (id) => {
    const isConfirmed = await confirm({ title: "Accept Vendor", description: "Are you sure you want to accept this vendor?", confirmText: "Accept" });
    if (!isConfirmed) return;
    try {
      const response = await acceptVenderApi({ token, id, vendorStatus: 2 });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.error || error.message || "Something went wrong");
    }
  };

  const handleRejectVendor = async (id) => {
    if (!rejectMessage) {
      toast.warning("Reason Required");
      return;
    }
    const isConfirmed = await confirm({ title: "Reject Vendor", description: "Are you sure you want to reject this vendor?", confirmText: "Reject", destructive: true });
    if (!isConfirmed) return;
    try {
      const response = await rejectVenderApi({ token, id, vendorStatus: 3, rejectReason: rejectMessage });
      setRejectMessage("");
      setChange(!change);
      toast.success(response.message);
      setRejectModalOpen(false);
      setOpen(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const columns = [
    {
      field: "fullname",
      headerName: "Business Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar
            src={row.picture ? `${configuration.cdnFront}/${row.picture}` : undefined}
            name={row.fullname}
            size={36}
            sx={{ flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    { field: "email", headerName: "Email", minWidth: 160, renderCell: (r) => r.email || "-" },
    { field: "phone", headerName: "Mobile No", width: 120, renderCell: (r) => r.phone || "-" },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      renderCell: (r) =>
        r.address ? [r.address.address || "-"].filter(Boolean).join(", ") : "-",
    },
    {
      field: "vendorStatus",
      headerName: "Status",
      width: 100,
      renderCell: (r) => <StatusChip value={r.vendorStatus} />,
    },
    {
      field: "__actions",
      headerName: "",
      width: 200,
      align: "right",
      renderCell: (row) => {
        const stripeIncomplete = row.stripeOnboardingComplete === false;
        return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            onClick={() => { setSelectedRow(row); setOpen(true); }}
            sx={{ color: "#065F46" }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <Tooltip title={stripeIncomplete ? "Vendor has not completed Stripe onboarding" : ""} arrow>
            <span>
          <Button
            onClick={() => handleAcceptVendor(row._id)}
            disabled={stripeIncomplete}
            size="small"
            sx={{
              borderRadius: "10px",
              bgcolor: stripeIncomplete ? "#B0B0B0" : "#065F46",
              color: "#FFF",
              textTransform: "none",
              fontSize: "12px",
              px: 1.5,
              minWidth: 0,
              "&:hover": { bgcolor: stripeIncomplete ? "#B0B0B0" : "#065F46" },
              "&.Mui-disabled": { bgcolor: "#B0B0B0", color: "#FFF" },
            }}
          >
            Accept
          </Button>
            </span>
          </Tooltip>
          <Button
            onClick={() => { setSelectedRow(row); setRejectModalOpen(true); }}
            size="small"
            sx={{
              borderRadius: "10px",
              bgcolor: "#EA001B",
              color: "#FFF",
              textTransform: "none",
              fontSize: "12px",
              px: 1.5,
              minWidth: 0,
              "&:hover": { bgcolor: "#EA001B" },
            }}
          >
            Reject
          </Button>
        </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ marginTop: "40px", marginBottom: "30px" }}>
      <AdminTable
        columns={columns}
        rows={pendingVendorData}
        loading={loading}
        rowKey="_id"
        emptyText="No pending vendors"
      />
      <Paginations
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
        disabled={loading}
      />

      {/* View modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            maxHeight: "75vh",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "681px",
            width: "90%",
            bgcolor: "#FFFFFF",
            borderRadius: "32px",
            boxShadow: 24,
            overflowY: "auto",
            p: 4,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Typography sx={{ marginBottom: "16px", color: "#362B24", fontSize: "20px", fontWeight: "700" }}>
              Vendor Detail
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedRow && (
            <Box sx={{ marginTop: 1 }}>
              {selectedRow?.stripeOnboardingComplete === false && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#FFFBEB",
                    border: "1px solid #F59E0B",
                    borderRadius: "12px",
                    px: 2,
                    py: 1.25,
                    mb: 2,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "#D97706", fontSize: 18, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "13px", color: "#92400E", fontFamily: "Heebo" }}>
                    This vendor has not completed Stripe onboarding and cannot be accepted yet.
                  </Typography>
                </Box>
              )}
              <Box sx={{ textAlign: "center" }}>
                <UserAvatar
                  src={selectedRow?.picture ? `${configuration.cdnFront}/${selectedRow.picture}` : undefined}
                  name={selectedRow?.fullname}
                  size={100}
                  sx={{ mx: "auto", mb: "10px" }}
                />
                <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#362B24", marginBottom: "8px", marginTop: "10px" }}>
                  {selectedRow?.fullname || "-"}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "16px", color: "#065F46", fontSize: "14px" }}>
                  <EmailRoundedIcon sx={{ marginRight: "4px" }} />
                  <Typography>{selectedRow?.email || "-"}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "8px", color: "#065F46", fontSize: "14px" }}>
                  <CallIcon sx={{ marginRight: "4px" }} />
                  <Typography>{selectedRow?.phone || "-"}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 2, marginTop: "30px" }}>
                {[
                  { label: "Address", value: selectedRow?.address?.address },
                  { label: "City", value: selectedRow?.address?.city },
                  { label: "State", value: selectedRow?.address?.state },
                  { label: "Country", value: selectedRow?.address?.country },
                  { label: "Zip Code", value: selectedRow?.address?.zipcode },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24" }}>{label}</Typography>
                    <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>{value || "-"}</Typography>
                  </Box>
                ))}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", marginTop: "20px", color: "#362B24", fontSize: "15px" }}>Description</Typography>
                <Typography sx={{ fontWeight: "400", fontSize: { xs: "12px", sm: "13px", md: "16px" }, color: "#362B24", opacity: 0.6, marginTop: "8px" }}>
                  {selectedRow?.bio || "N/A"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginTop: "25px" }}>
                <Tooltip title={selectedRow?.stripeOnboardingComplete === false ? "Vendor has not completed Stripe onboarding" : ""} arrow>
                  <span>
                <Button
                  onClick={() => { handleAcceptVendor(selectedRow._id); setOpen(false); }}
                  disabled={selectedRow?.stripeOnboardingComplete === false}
                  sx={{
                    width: "180px", height: "50px", borderRadius: "15px",
                    bgcolor: selectedRow?.stripeOnboardingComplete === false ? "#B0B0B0" : "#065F46",
                    color: "#FFF", textTransform: "none",
                    "&:hover": { bgcolor: selectedRow?.stripeOnboardingComplete === false ? "#B0B0B0" : "#065F46" },
                    "&.Mui-disabled": { bgcolor: "#B0B0B0", color: "#FFF" },
                  }}
                >
                  Accept
                </Button>
                  </span>
                </Tooltip>
                <Button
                  onClick={() => setRejectModalOpen(true)}
                  sx={{ width: "180px", height: "50px", borderRadius: "15px", bgcolor: "#EA001B", color: "#FFF", textTransform: "none", "&:hover": { bgcolor: "#EA001B" } }}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Reject reason modal */}
      <Modal open={rejectModalOpen} onClose={() => setRejectModalOpen(false)}>
        <Box
          sx={{
            maxWidth: "538px",
            width: "90%",
            height: "418px",
            borderRadius: "32px",
            bgcolor: "#FFF",
            display: "flex",
            flexDirection: "column",
            p: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ color: "#362B24", fontSize: "20px", fontWeight: "700" }}>
              Reason for Rejection
            </Typography>
            <IconButton onClick={() => setRejectModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography sx={{ color: "#362B24", marginTop: "15px", fontSize: "14px", fontWeight: "600" }}>
            Type your Reason
          </Typography>

          <textarea
            placeholder="Type here"
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setRejectMessage(e.target.value);
              }
            }}
            style={{
              maxWidth: "470px",
              width: "100%",
              height: "202px",
              borderRadius: "18px",
              border: "1.4px solid #362B24",
              backgroundColor: "#FFF",
              padding: "16px",
              fontSize: "16px",
              resize: "none",
              marginTop: "15px",
              outline: "none",
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              onClick={() => handleRejectVendor(selectedRow?._id)}
              sx={{
                width: "196px",
                height: "50px",
                borderRadius: "15px",
                bgcolor: "#EA001B",
                color: "#FFF",
                textTransform: "none",
                "&:hover": { bgcolor: "#EA001B" },
              }}
            >
              Reject
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PendingApprove;
